import type { SceneContentBaseline, SceneContentRequest, SceneEditMode, SceneFieldPatch, SceneFileKind } from '../../apis/simulation.ts';
import { isCloudCmdPolicy } from '../../utils/cloudCmdPolicy.ts';

export interface FileDraft {
  name: string;
  originalContent: string;
  content: string;
  baseSha256: string;
  kind: SceneFileKind;
  error: string | null;
}

const sharedKeys: Record<string, keyof SceneFieldPatch> = {
  start_time: 'sceneStartTime', end_time: 'sceneEndTime', map_name: 'mapName',
  map_version: 'mapVersion', logsim_label: 'label', description: 'description',
  issue_time: 'issueTime', open_switch_closed_loop_sec: 'openSwitchClosedLoopSec',
  mask_finish_signal: 'maskFinishSignal',
};

// 云控指令合并策略在 scene_extra_config.json（与云端控制事件 events 同文件）；文件里缺省 = passthrough。
const filePolicy = (doc: Record<string, unknown>) => (doc.cloud_cmd_policy ?? 'passthrough');

export function fileError(name: string, content: string, limits: SceneContentBaseline['limits'], originalContent?: string): string | null {
  if (content.includes('\0')) return '文本不能包含 NUL 字符';
  const managed = ['scene_info.json', 'scene_extra_config.json', 'sim_perception_filter.json'].includes(name);
  if (new TextEncoder().encode(content).length > (managed ? limits.managedFileBytes : limits.fileBytes)) return '文件超过编辑大小限制';
  if (name.toLowerCase().endsWith('.pb.txt') && !content.trim()) return 'Protobuf 文本配置不能为空';
  if (name.toLowerCase().endsWith('.json')) {
    try {
      const parsed = JSON.parse(content);
      if (managed && (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))) return '配置文件必须是 JSON 对象';
      if (name === 'scene_info.json') {
        if (originalContent != null) {
          const original = JSON.parse(originalContent);
          for (const key of ['start_time', 'end_time', 'name', 'car_name', 'record_s3', 'task_type', 'jira_issue_code', 'scene_version']) {
            if (JSON.stringify(parsed[key]) !== JSON.stringify(original[key])) {
              return key === 'start_time' || key === 'end_time'
                ? '起止时间会影响场景生成，请在场景信息中修改。'
                : `${key} 由平台管理，不允许通过场景文件修改。`;
            }
          }
        }
        for (const key of Object.keys(sharedKeys)) {
          if (!(key in parsed) || parsed[key] == null) continue;
          if (key === 'open_switch_closed_loop_sec') {
            if (typeof parsed[key] !== 'number' || !Number.isFinite(parsed[key])) return `${key} 必须是有限数值`;
          } else if (key === 'mask_finish_signal') {
            if (typeof parsed[key] !== 'boolean') return `${key} 必须是布尔值`;
          } else if (typeof parsed[key] !== 'string') return `${key} 必须是字符串`;
        }
      }
      if (name === 'scene_extra_config.json' && 'events' in parsed && !Array.isArray(parsed.events)) return 'events 必须是数组';
      if (name === 'scene_extra_config.json' && 'cloud_cmd_policy' in parsed && !isCloudCmdPolicy(parsed.cloud_cmd_policy)) {
        return 'cloud_cmd_policy 必须是 passthrough / override / mask_all 之一';
      }
    } catch (error) {
      return error instanceof Error ? error.message : 'JSON 格式错误';
    }
  }
  return null;
}

export function fieldsFromFile(name: string, content: string, previous: string): SceneFieldPatch {
  if (name !== 'scene_info.json' && name !== 'scene_extra_config.json') return {};
  const value = JSON.parse(content);
  let before: Record<string, unknown> = {};
  try { before = JSON.parse(previous); } catch { /* a repaired document */ }
  if (name === 'scene_extra_config.json') {
    const extra: Record<string, unknown> = {};
    if (JSON.stringify(value.events ?? []) !== JSON.stringify(before.events ?? [])) extra.events = value.events ?? [];
    if (filePolicy(value) !== filePolicy(before)) extra.cloudCmdPolicy = filePolicy(value);
    return extra as SceneFieldPatch;
  }
  const patch: Record<string, unknown> = {};
  for (const [jsonKey, field] of Object.entries(sharedKeys)) {
    if (JSON.stringify(value[jsonKey]) !== JSON.stringify(before[jsonKey])) {
      patch[field] = value[jsonKey] ?? (field === 'label' || field === 'description' ? '' : null);
    }
  }
  return patch as SceneFieldPatch;
}

export function patchFileFields(file: FileDraft, fields: SceneFieldPatch, limits: SceneContentBaseline['limits']): FileDraft {
  if (file.error || !['scene_info.json', 'scene_extra_config.json'].includes(file.name)) return file;
  const value = JSON.parse(file.content);
  let changed = false;
  if (file.name === 'scene_info.json') {
    for (const [jsonKey, field] of Object.entries(sharedKeys)) {
      if (field in fields && JSON.stringify(value[jsonKey]) !== JSON.stringify(fields[field])) {
        value[jsonKey] = fields[field];
        changed = true;
      }
    }
  } else {
    if ('events' in fields && JSON.stringify(value.events ?? []) !== JSON.stringify(fields.events ?? [])) {
      value.events = fields.events ?? [];
      changed = true;
    }
    if ('cloudCmdPolicy' in fields) {
      const next = fields.cloudCmdPolicy ?? 'passthrough';
      if (filePolicy(value) !== next) {
        // 回到默认（passthrough）= 摘掉 key，与后端回写口径一致。
        if (next === 'passthrough') delete value.cloud_cmd_policy; else value.cloud_cmd_policy = next;
        changed = true;
      }
    }
  }
  if (!changed) return file;
  // Restoring a field restores the original bytes when the entire document is back
  // to its original value, avoiding a formatting-only dirty flag on cancel/reset.
  let original: unknown;
  try { original = JSON.parse(file.originalContent); } catch { original = null; }
  const content = JSON.stringify(value) === JSON.stringify(original) ? file.originalContent : JSON.stringify(value, null, 2);
  return { ...file, content, error: fileError(file.name, content, limits) };
}

export function fieldPatch(current: SceneFieldPatch, original: SceneFieldPatch): SceneFieldPatch {
  const result: Record<string, unknown> = {};
  for (const key of [...Object.values(sharedKeys), 'events', 'cloudCmdPolicy'] as (keyof SceneFieldPatch)[]) {
    const value = current[key];
    if (JSON.stringify(comparableField(key, value)) !== JSON.stringify(comparableField(key, original[key]))) result[key] = value;
  }
  return result as SceneFieldPatch;
}

export function changedFiles(files: Record<string, FileDraft>): FileDraft[] {
  return Object.values(files).filter(file => file.content !== file.originalContent);
}

function comparableField(key: keyof SceneFieldPatch, value: unknown): unknown {
  if (key === 'events') return value ?? [];
  if (key === 'maskFinishSignal') return value ?? false;
  if (key === 'cloudCmdPolicy') return value ?? 'passthrough';
  if (typeof value === 'string') {
    const text = value.trim();
    if (['sceneStartTime', 'sceneEndTime', 'issueTime'].includes(key)) {
      const match = text.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.(\d+))?$/);
      if (match) return `${match[1]} ${match[2]}.${((match[3] ?? '') + '000').slice(0, 3)}`;
    }
    return text || null;
  }
  return value ?? null;
}

/** Only the side changed by the user owns the edit; the other side is a preview. */
export function editModeAfterChange(current: SceneEditMode | null, source: SceneEditMode, dirty: boolean): SceneEditMode | null {
  if (current && current !== source) return current;
  return dirty ? source : null;
}

export function fileFieldPreview(files: Record<string, FileDraft>, original: SceneFieldPatch): SceneFieldPatch {
  return Object.values(files).reduce((fields, file) => file.error ? fields
    : { ...fields, ...fieldsFromFile(file.name, file.content, file.originalContent) }, { ...original });
}

export function contentPayload(
  baseline: SceneContentBaseline, editMode: SceneEditMode | null,
  fields: SceneFieldPatch, files: Record<string, FileDraft>,
): Omit<SceneContentRequest, 'requestId'> {
  if (!editMode) throw new Error('没有需要保存的修改');
  const drafts = editMode === 'files' ? changedFiles(files) : [];
  const invalid = drafts.find(file => file.error);
  if (invalid) throw new Error(`${invalid.name}: ${invalid.error}`);
  if (drafts.length > baseline.limits.fileCount) throw new Error(`一次最多修改 ${baseline.limits.fileCount} 个文件`);
  if (drafts.reduce((sum, file) => sum + new TextEncoder().encode(file.content).length, 0) > baseline.limits.totalBytes) throw new Error('本次文件内容总量超过限制');
  return {
    editMode, baseVersion: baseline.baseVersion, fields: editMode === 'fields' ? fields : {},
    files: drafts.map(file => ({ name: file.name, baseSha256: file.baseSha256, content: file.content })),
  };
}

export function timeRangeChanged(fields: SceneFieldPatch, original: SceneFieldPatch): boolean {
  const patch = fieldPatch({ ...original, ...fields }, original);
  return 'sceneStartTime' in patch || 'sceneEndTime' in patch;
}

/** A generation observed during editing invalidates that draft, even after it ends. */
export function requiresDraftReload(wasStale: boolean, busy: boolean, baseVersion: number, latestVersion: number): boolean {
  return wasStale || busy || baseVersion !== latestVersion;
}

/** 更新记录里一条字段改动的展示形态:一条「工序轨道」,每段标清是谁动的。
 *
 *  时间字段在「改时间触发的重跑」链路上最多三段:原值 → 用户调整(requested)
 *  → 引擎调整(after)。**只渲染真的发生过变化的段**（2026-10-19 口径）:
 *
 *  - ``userChanged``:用户真的改过这个字段。``engineOnly`` 的 ``requested`` 是引擎
 *    第一次回调时的兜底值（``_merge_time_rerun_diff`` 二次回调补写的）,不算用户改的。
 *  - ``engineAdjusted``:引擎最终值 ≠ 上一段的值 —— 只有它才渲染「引擎调整」那一段。
 *    引擎没再动过的字段少一段,免得出现「…095 → …095 保持」这种看着像没变、还自相
 *    矛盾的渲染。
 *  - ``finalActor``:最后一段的值是谁产出的 → 决定 caption（引擎调整 / 用户调整 /
 *    新值）。老记录与普通编辑没有阶段信息,不硬套标签,一律「新值」。 */
export interface VersionDiffView {
  before: unknown;
  requested: unknown;
  after: unknown;
  hasRequested: boolean;
  userChanged: boolean;
  engineAdjusted: boolean;
  finalActor: 'engine' | 'user' | null;
}

export function versionDiffView(diff: {
  before?: unknown; after?: unknown; requested?: unknown; engineOnly?: boolean;
} | null | undefined): VersionDiffView {
  const requested = diff?.requested;
  const hasRequested = requested !== undefined && requested !== null;
  const engineOnly = diff?.engineOnly === true;
  // engineOnly 的 requested 是引擎自己兜底填的,不能当成「用户调整」那一段。
  const userChanged = hasRequested && !engineOnly;
  const engineAdjusted = engineOnly
    ? diff?.after !== diff?.before
    : userChanged && diff?.after !== requested;
  return {
    before: diff?.before,
    requested,
    after: diff?.after,
    hasRequested,
    userChanged,
    engineAdjusted,
    finalActor: engineAdjusted ? 'engine' : userChanged ? 'user' : null,
  };
}

/** 结构化值展开后的一行:一个**叶子键**的改动。
 *
 *  ``key`` 是叶子键本身（`external.jira.issue_time` → ``issue_time``）—— 展示名要跟
 *  「场景信息」面板的字段名对齐,就得按叶子键去查,不能按路径。
 *  ``path`` 是到叶子的完整路径,只在叶子键没有对应展示名时兜底显示（如 ``params.xxx``）。 */
export interface ObjectDiffRow {
  key: string;
  path: string[];
  before: unknown;
  requested: unknown;
  after: unknown;
}

/** 结构化值（对象）的逐字段差异行,嵌套对象一路下钻到叶子。
 *
 *  `external` / `params` 这类字段,后端 `changed_fields` 里存的是**整个对象**的
 *  before/after。实测两种形态都会踩:
 *
 *  - 改「开环切闭环秒数」→ ``external.{before:{open…:0}, after:{open…:2}}``
 *  - 改「故障时刻」→ 它落在 ``external.jira.issue_time``,差量是
 *    ``external.{before:{open…:2}, after:{jira:{issue_time:'…'}, open…:2}}`` ——
 *    只展开一层的话会渲染成「jira →（结构化变更）」,用户照样看不出改了哪个字段。
 *
 *  所以这里递归下钻,只把**真的变了的叶子**铺出来;值仍交给 `versionDiffView`
 *  走同一套两/三段轨道。 */
export function objectDiffRows(
  before: unknown, requested: unknown, after: unknown,
): ObjectDiffRow[] {
  const rows: ObjectDiffRow[] = [];
  collectObjectDiff(before, requested, after, [], rows);
  return rows;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function collectObjectDiff(
  before: unknown, requested: unknown, after: unknown,
  path: string[], rows: ObjectDiffRow[],
): void {
  const keys = new Set<string>();
  [before, requested, after].forEach((obj) => {
    if (isPlainObject(obj)) Object.keys(obj).forEach((key) => keys.add(key));
  });
  if (keys.size === 0) return;
  [...keys].sort((a, b) => a.localeCompare(b)).forEach((key) => {
    const b = isPlainObject(before) ? before[key] : undefined;
    const a = isPlainObject(after) ? after[key] : undefined;
    // 中间的「用户调整」只在这次改动真的记了 requested 且**带这个键**时才算一段；
    // 没记（普通编辑 / 老记录）就拿原值和新值比 —— 否则每个没变的键都会因为
    // `requested[key] === undefined` 被判成「变过」,整张对象全铺出来。
    const tracked = isPlainObject(requested) && key in requested;
    const r = tracked && isPlainObject(requested) ? requested[key] : undefined;
    const nextPath = [...path, key];
    // 任意一段还是对象 → 继续下钻（一侧新增出整个对象时也算）
    if (isPlainObject(b) || isPlainObject(a) || isPlainObject(r)) {
      collectObjectDiff(b, tracked ? r : undefined, a, nextPath, rows);
      return;
    }
    if (sameValue(b, a) && (!tracked || sameValue(b, r))) return;
    rows.push({ key, path: nextPath, before: b, requested: tracked ? r : undefined, after: a });
  });
}

/** 引擎回调带回的原因（平台 `_normalize_resolve_reason` 的规整结果）。 */
export interface EngineReason {
  code?: string | null;
  text?: string | null;
  detail?: string | null;
  stage?: string | null;
  at?: string | null;
  sceneStatus?: string | null;
}

/** 挑出「展示用」的引擎调整原因。

 *  - 重跑链路优先：`timeRerun.reason` 是那条链路专门维护的展示值；
 *  - 其余链路（首建 / 重新构建 / QC 定稿）看 `source_ref.resolveReasons` 流水，
 *    **从后往前**找最近一条带已知分支码或 detail 的；
 *  - `code` 为空又没有 `detail` 的纯自由文本（`scene_qc:pass (mileage=…)` 是引擎
 *    自己的审计串）不展示 —— 它回答不了「时间为什么变了」（2026-10-19 口径）；
 *  - 都没有 → ``null``,调用方不渲染结论行。 */
export function pickEngineReason(
  timeRerunReason: EngineReason | null | undefined,
  resolveReasons: EngineReason[] | null | undefined,
): EngineReason | null {
  const usable = (r: EngineReason | null | undefined): r is EngineReason =>
    !!r && (!!r.code || !!r.detail);
  if (usable(timeRerunReason)) return timeRerunReason;
  const history = Array.isArray(resolveReasons) ? resolveReasons : [];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (usable(history[i])) return history[i];
  }
  return null;
}
